/**
 * Cross-origin fetch shim for the desktop native shell.
 *
 * The renderer's origin under Electron is `app://localhost`, which is
 * not whitelisted by the remote APIs we talk to (WorkOS, the Quadratic
 * API, S3, etc.). A direct `window.fetch` is therefore CORS-rejected
 * before the request leaves the WebView, even though the server returns
 * 200.
 *
 * Electron exposes a main-process HTTP client that bypasses CORS:
 * `window.electron.fetch(...)` invokes Electron's `net.fetch` over IPC
 * (see `quadratic-electron/src/netFetch.ts`).
 *
 * `installNativeFetchShim()` patches `window.fetch` once at bootstrap
 * to route only cross-origin HTTP(S) requests through the native
 * client, leaving same-origin and non-HTTP schemes (`blob:`, `data:`,
 * `app:`) on the native browser fetch path.
 *
 * No-op on regular web builds.
 */
import { isElectron } from '@/app/nativeBridge';

let installed = false;

const extractUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

// Returns true when a request is cross-origin HTTP(S) and therefore
// needs a CORS-bypassing native client. Same-origin, custom schemes,
// `data:`, `blob:`, etc. are left on the browser fetch.
const shouldProxy = (url: string): boolean => {
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    if (u.origin === window.location.origin) return false;
    return true;
  } catch {
    return false;
  }
};

const headersToPairs = (headers: HeadersInit | undefined): Array<[string, string]> => {
  if (!headers) return [];
  if (headers instanceof Headers) return Array.from(headers.entries());
  if (Array.isArray(headers)) return headers.map(([k, v]) => [String(k), String(v)]);
  return Object.entries(headers).map(([k, v]) => [k, String(v)]);
};

const bodyToArrayBuffer = async (body: BodyInit | null | undefined): Promise<ArrayBuffer | undefined> => {
  if (body == null) return undefined;
  if (body instanceof ArrayBuffer) return body;
  if (ArrayBuffer.isView(body)) {
    const view = body as ArrayBufferView;
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
  }
  if (body instanceof Blob) return await body.arrayBuffer();
  if (typeof body === 'string') return new TextEncoder().encode(body).buffer as ArrayBuffer;
  // URLSearchParams / FormData / ReadableStream fall back to Request so
  // the browser handles the tricky serialization for us.
  return await new Response(body).arrayBuffer();
};

const electronFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const url = extractUrl(input);
  const request = input instanceof Request ? input : new Request(url, init);
  const method = init?.method ?? request.method ?? 'GET';
  const headers = headersToPairs(init?.headers ?? request.headers);
  // Fall back to the Request body when a caller passes a `Request` with a body
  // but no `init` (otherwise the body would be silently dropped).
  const rawBody =
    init?.body ??
    (input instanceof Request
      ? await input
          .clone()
          .arrayBuffer()
          .catch(() => undefined)
      : undefined);
  const body =
    method === 'GET' || method === 'HEAD' ? undefined : await bodyToArrayBuffer(rawBody as BodyInit | null | undefined);

  if (!window.electron) {
    throw new Error('electronFetch called without electron bridge');
  }
  const result = await window.electron.fetch({ url, method, headers, body });
  const responseHeaders = new Headers(result.headers);
  return new Response(result.body, {
    status: result.status,
    statusText: result.statusText,
    headers: responseHeaders,
  });
};

export const installNativeFetchShim = (): void => {
  if (installed) return;
  if (!isElectron()) return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = extractUrl(input);
    if (!shouldProxy(url)) {
      return nativeFetch(input as RequestInfo, init);
    }
    return electronFetch(input, init);
  };
};
